import h from 'hyperscript';
import flatpickr from 'flatpickr';
import locale from 'flatpickr/dist/l10n/de';
import 'flatpickr/dist/flatpickr.css';
import calendarIcon from '@disy/cadenza-icons/calendar.svg';
import clearIcon from '@disy/cadenza-icons/close.svg';
import chevronRightIcon from '@disy/cadenza-icons/chevron-right.svg';
import chevronLeftIcon from '@disy/cadenza-icons/chevron-left.svg';

import { createHorizontalSeparator } from 'ui/separator/separator';
import { createIconButton } from 'ui/button/button';
import { setupPopupButton } from 'ui/popup/popup';

import { icon } from 'cadenza/utils/icon/icon';
import { mediaSmUp } from 'cadenza/utils/media';
import {
  formatDateWithJavaFormat,
  parseAndValidateDateWithJavaFormat
} from 'cadenza/utils/date-time/date-format';
import { getCurrentLanguage } from 'cadenza/utils/i18n/i18n';
import { MonthSelectPlugin } from 'cadenza/conditions/date-picker/flatpickr-month-select-plugin';
import { YearSelectPlugin } from 'cadenza/conditions/date-picker/flatpickr-year-select-plugin';
import { TickUnit, TickUnitOrder } from 'cadenza/conditions/date-time-condition/date-time-condition-utils';
import type { FlatpickrAbstractPlugin } from 'cadenza/conditions/date-picker/flatpickr-abstract-plugin';

import type { key as LocaleKey } from 'flatpickr/dist/types/locale';
import type { Options } from 'flatpickr/dist/types/options';

import './date-range-picker.css';
import i18n from './date-range-picker.properties';

export const COMPONENT_NAME = 'd-date-range-picker';

export interface DateRangePickerOption {
  initialStartDate: Date;
  initialEndDate: Date;
  minDate: Date;
  maxDate: Date;
  dateFormat?: string;
  mostFineGrainedTickUnit?: string;
  popupMode?: boolean;
}

type ActiveDateType = 'START' | 'END';

export const DATE_RANGE_CHANGE = 'change:dateRange';

export class DateRangePicker extends HTMLElement {

  _dateFormat;
  _initialStartDate;
  _initialEndDate;
  _minDate;
  _maxDate;
  _granularTickUnitOrder;

  _rangeInputs;
  _dateTimePicker;

  _selectedStartDate;
  _selectedEndDate;
  _activeDateType?: ActiveDateType;

  _startDatePicker;
  _endDatePicker;
  _labelBetweenStartAndEnd: HTMLElement | null;
  _shortLabelText = '';
  _longLabelText = '';
  _activeDatePickerInput?: HTMLInputElement;
  _calendarTitle;
  _flatpickr;
  _flatpickrPlugin?: FlatpickrAbstractPlugin;
  _hourInput;
  _minuteInput;
  _secondInput;

  _calendar;
  _separator;
  _timePicker;

  _popupMode;

  constructor (
    {
      initialStartDate,
      initialEndDate,
      minDate,
      maxDate,
      dateFormat = 'dd.MM.yyyy',
      mostFineGrainedTickUnit = TickUnit.DAYS,
      popupMode = false
    }: DateRangePickerOption
  ) {
    super();

    this._dateFormat = dateFormat;
    this._initialStartDate = initialStartDate;
    this._initialEndDate = initialEndDate;
    this._selectedStartDate = initialStartDate ? new Date(initialStartDate) : undefined;
    this._selectedEndDate = initialEndDate ? new Date(initialEndDate) : undefined;

    this._minDate = minDate;
    this._maxDate = maxDate;

    this._granularTickUnitOrder = TickUnitOrder[mostFineGrainedTickUnit];
    this._popupMode = popupMode;

    this._startDatePicker = this._createDatePicker(true);
    this._endDatePicker = this._createDatePicker(false);
    this._labelBetweenStartAndEnd = this._createLabelBetweenStartAndEnd();
    this._rangeInputs = h(`.d-stack-h.${popupMode
      ? 'd-date-range-picker.is-popup'
      : 'space-3'}`, [
      this._startDatePicker,
      this._labelBetweenStartAndEnd,
      this._endDatePicker
    ]);
    const flatPickerContainer: HTMLElement = h(`.${COMPONENT_NAME}--flatpickr-container`);
    this._flatpickr = this._createFlatpickr(flatPickerContainer);
    this._calendarTitle = h(`label.${COMPONENT_NAME}--title${popupMode ? '.is-popup' : ''}`);
    this._calendar = h(
      `.${COMPONENT_NAME}--calendar${popupMode
        ? '.is-popup'
        : ''}`, [ this._calendarTitle, flatPickerContainer ]);
    const activeTimeElements = [];
    if (this._isTickUnitEnabled(TickUnit.HOURS)) {
      const { container, input } = this._createTimeElements(i18n('dateRangePicker.hour'),
        23,
        () => this._getActiveDate()?.getHours(),
        (v) => this._getActiveDate()?.setHours(v));
      this._hourInput = input;
      activeTimeElements.push(container);
    }
    if (this._isTickUnitEnabled(TickUnit.MINUTES)) {
      const { container, input } = this._createTimeElements(i18n('dateRangePicker.minute'),
        59,
        () => this._getActiveDate()?.getMinutes(),
        (v) => this._getActiveDate()?.setMinutes(v));
      this._minuteInput = input;
      activeTimeElements.push(container);
    }
    if (this._isTickUnitEnabled(TickUnit.SECONDS)) {
      const { container, input } = this._createTimeElements(i18n('dateRangePicker.second'),
        59,
        () => this._getActiveDate()?.getSeconds(),
        (v) => this._getActiveDate()?.setSeconds(v));
      this._secondInput = input;
      activeTimeElements.push(container);
    }
    const additionalClassForPopup = popupMode ? '.is-time-picker-popup' : '';
    this._timePicker = activeTimeElements.length > 0
      ? h(`.d-stack-h.space-0_25${additionalClassForPopup}`, activeTimeElements)
      : null;
    this._separator = createHorizontalSeparator();
    this._dateTimePicker = h('.d-stack-v.space-3', [
      this._calendar,
      this._separator,
      this._timePicker ]);
  }

  connectedCallback () {
    this.classList.add(COMPONENT_NAME);

    if (!this._popupMode) {
      this.append(h('.d-stack-v', [ this._rangeInputs, this._dateTimePicker ]));
    } else {
      this.classList.add('is-date-picker-popup');
      // this._dateTimePicker will be added as popup content
      this.append(h('.d-stack-v', [ this._rangeInputs ]));
      mediaSmUp.addEventListener('change', this._onMediaSmUpChanged.bind(this));
    }

    this._updateVisibility();
  }

  disconnectedCallback () {
    mediaSmUp.removeEventListener('change', this._onMediaSmUpChanged);
  }

  _createLabelBetweenStartAndEnd () {
    if (!this._popupMode) {
      if (!this._hasTime()) {
        return h(`label.${COMPONENT_NAME}--label`, i18n('dateRangePicker.until'));
      } else {
        return null; // Displaying label does take up too much space.
      }
    }
    return h('label');
  }

  _createDatePicker (isStart: boolean): HTMLElement {
    const date = isStart ? this._initialStartDate : this._initialEndDate;
    const value = date ? formatDateWithJavaFormat(date, this._dateFormat) : '';
    const input: HTMLInputElement = h('input.form-control', {
      type: 'text',
      placeholder: formatDateWithJavaFormat(isStart ? this._minDate : this._maxDate, this._dateFormat),
      value,
      onfocus: () => {
        if (this._calendar.hasAttribute('hidden')) {
          this._updateCalendarActiveState(isStart, input);
        } else if (this._activeDatePickerInput !== input) {
          this._onCalendarSwitch(isStart, input);
        }
      },
      onchange: (event: Event) => this._onDateFinishedInput((event.target as HTMLInputElement).value, isStart),
      oninput: () => {
        clearButton.hidden = !input.value;
      }
    });
    const styleClasses = [ `${COMPONENT_NAME}--clear-button` ];
    if (this._popupMode) {
      styleClasses.push('is-popup');
    }
    const clearButton = createIconButton(icon(clearIcon),
      i18n('dateRangePicker.clear'),
      {
        variant: 'borderless',
        size: 'xs',
        styleClass: styleClasses,
        hidden: !value,
        onclick: () => {
          if (this._calendar.hasAttribute('hidden')) {
            this._updateCalendarActiveState(isStart, input);
          } else if (this._activeDatePickerInput !== input) {
            this._onCalendarSwitch(isStart, input);
          }
          this._onDateFinishedInput('', isStart);
        }
      }
    );
    const calendarButton = createIconButton(icon(calendarIcon),
      i18n('dateRangePicker.calendarTitle'),
      {
        variant: 'borderless',
        styleClass: `${COMPONENT_NAME}--calendar-button`,
        onclick: () => this._onCalendarSwitch(isStart, input)
      });

    if (this._popupMode) {
      setupPopupButton(calendarButton, () => h('.d-stack-v', [ this._dateTimePicker ]));
    }
    const datePicker: HTMLElement = h(`.${COMPONENT_NAME}--date-picker`, { tabIndex: 0 },
      [ h(`.${COMPONENT_NAME}--date-picker-input`, [ input, clearButton ]), calendarButton ]);
    const datePickerWidth =
        `calc(${this._dateFormat.length}ch + 20px + var(--button-size-m))`;
    datePicker.style.width = datePickerWidth;
    datePicker.style.minWidth = datePickerWidth;
    return datePicker;
  }

  _createFlatpickr (appendTo: HTMLElement) {
    const flatPickrConfig: Options = {
      inline: true,
      locale: { ...locale[getCurrentLanguage() as LocaleKey] },
      plugins: [],
      prevArrow: createIconButton(icon(chevronLeftIcon, { size: 'l' }),
        i18n('dateRangePicker.prev'),
        { variant: 'borderless' }).outerHTML,
      nextArrow: createIconButton(icon(chevronRightIcon, { size: 'l' }),
        i18n('dateRangePicker.next'),
        { variant: 'borderless' }).outerHTML,
      onChange: ([ selectedDate ]: Date[]) => {
        // it can be null because of the custom plugin
        if (selectedDate) {
          let activeDate = this._getActiveDate();
          const oldTimeStamp = activeDate?.getTime();
          if (activeDate) {
            activeDate.setFullYear(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
          } else {
            activeDate = selectedDate;
            this._setActiveDate(activeDate);
          }
          if (oldTimeStamp !== activeDate.getTime()) {
            this._setDatePickerInputValue(activeDate);
            this._onChange();
          }
        }
      },
      appendTo
    };

    if (this._isYearMonth()) {
      this._flatpickrPlugin = new MonthSelectPlugin();
      flatPickrConfig.plugins = [ this._flatpickrPlugin.factory() ];
    } else if (this._isYearOnly()) {
      this._flatpickrPlugin = new YearSelectPlugin();
      flatPickrConfig.plugins = [ this._flatpickrPlugin.factory() ];
    }
    const input = h('input.visuallyhidden');
    appendTo.append(input);
    return flatpickr(input, flatPickrConfig);
  }

  _createTimeElements (title: string, max: number, oldValueCallback: () => number | undefined, onChangeCallback: (value: number) => void) {
    const input: HTMLInputElement = h('input.form-control', {
      type: 'number',
      min: 0,
      max,
      disabled: !this._getActiveDate(),
      onchange: () => this._onTimeFinishedInput(input, oldValueCallback(), onChangeCallback)
    });
    return {
      container: h(`.${COMPONENT_NAME}--time-picker`, [
        h(`label.${COMPONENT_NAME}--title`, title),
        h(`.${COMPONENT_NAME}--time-input-container`, input) ]),
      input
    };
  }

  _onDateFinishedInput (dateString: string, isStart: boolean) {
    let adjustedDate;
    const activeDate = this._getActiveDate();
    if (dateString) {
      const date = this._parseAndValidateDateString(dateString);
      if (date) {
        const { earlierDate, latestDate } = this._getAdjustedExtremeDates(isStart);
        adjustedDate = date;
        if (earlierDate && date < earlierDate) {
          adjustedDate = earlierDate;
        } else if (latestDate && date > latestDate) {
          adjustedDate = latestDate;
        }
      } else {
        adjustedDate = activeDate;
      }
    }
    this._setDatePickerInputValue(adjustedDate);
    const oldTimeStamp = activeDate?.getTime();
    if (oldTimeStamp !== adjustedDate?.getTime()) {
      this._setActiveDate(adjustedDate);
      this._setFlatPickerValue(adjustedDate);
      this._updateTimeInputs();
      this._onChange();
    }
  }

  _onTimeFinishedInput (input: HTMLInputElement, oldValue: number | undefined, onChangeCallback: (value: number) => void) {
    let adjustedValue = Number(input.value);
    const min = Number(input.min);
    const max = Number(input.max);
    if (adjustedValue < min) {
      adjustedValue = min;
    } else if (adjustedValue > max) {
      adjustedValue = max;
    }
    input.value = adjustedValue.toString();
    if (oldValue !== adjustedValue) {
      onChangeCallback(adjustedValue);
      this._setDatePickerInputValue(this._getActiveDate());
      this._onChange();
    }
  }

  _setDatePickerInputValue (date?: Date) {
    if (this._activeDatePickerInput) {
      (this._activeDatePickerInput.nextElementSibling as HTMLElement).hidden = !date;
      this._activeDatePickerInput.value = date
        ? formatDateWithJavaFormat(date, this._dateFormat)
        : '';
    }
  }

  _setFlatPickerValue (date?: Date) {
    if (date) {
      this._flatpickr.setDate(new Date(date));
    } else {
      this._flatpickr.clear(false);
      this._flatpickr.jumpToDate(this._activeDateType === 'START'
        ? this._minDate
        : this._maxDate);
    }
    if (this._flatpickrPlugin) {
      this._flatpickrPlugin.resetElements();
    }
  }

  _updateTimeInputs () {
    const activeDate = this._getActiveDate();
    if (this._hourInput) {
      this._hourInput.disabled = !activeDate;
      this._hourInput.value = activeDate?.getHours().toString() ?? '';
    }
    if (this._minuteInput) {
      this._minuteInput.disabled = !activeDate;
      this._minuteInput.value = activeDate?.getMinutes().toString() ?? '';
    }
    if (this._secondInput) {
      this._secondInput.disabled = !activeDate;
      this._secondInput.value = activeDate?.getSeconds().toString() ?? '';
    }
  }

  _onChange () {
    this.dispatchEvent(new CustomEvent(DATE_RANGE_CHANGE, {
      bubbles: true,
      detail: {
        isStart: this._activeDateType === 'START',
        value: this._getActiveDate()
      } }));
  }

  _onCalendarSwitch (isStart: boolean, input: HTMLInputElement) {
    const currentDatePicker = isStart ? this._startDatePicker : this._endDatePicker;
    if (!this._isPressed(currentDatePicker)) {
      this._updateCalendarActiveState(isStart, input);
      const { earlierDate, latestDate } = this._getAdjustedExtremeDates(isStart);
      this._flatpickr.set('minDate', earlierDate);
      this._flatpickr.set('maxDate', latestDate);
      this._setFlatPickerValue(this._getActiveDate());

      this._updateTimeInputs();

      this._calendarTitle.textContent = i18n(`dateRangePicker.${isStart ? 'start' : 'end'}${this._hasTime() ? 'Time' : ''}Title`);
    }
    this._toggleCalendar(isStart);
  }

  _updateCalendarActiveState (isStart: boolean, input: HTMLInputElement) {
    this._activeDatePickerInput = input;
    this._activeDateType = isStart ? 'START' : 'END';
  }

  _toggleCalendar (isStart: boolean) {
    this._startDatePicker.setAttribute('aria-pressed',
      (isStart && !this._isPressed(this._startDatePicker)).toString());
    this._endDatePicker.setAttribute('aria-pressed',
      (!isStart && !this._isPressed(this._endDatePicker)).toString());
    this._updateVisibility();
  }

  _updateVisibility () {
    if (!this._popupMode) { // In popup mode, the visibility gets handled by the popup.
      const isAnyDatePickerActive = this._isPressed(this._startDatePicker) || this._isPressed(this._endDatePicker);
      this._calendar.toggleAttribute('hidden', !isAnyDatePickerActive);
      if (this._timePicker) {
        this._timePicker.toggleAttribute('hidden', !isAnyDatePickerActive);
      }
      this._separator.toggleAttribute('hidden', !isAnyDatePickerActive || !this._timePicker);
    }
  }

  _setActiveDate (date?: Date) {
    if (this._activeDateType === 'START') {
      this._selectedStartDate = date;
    } else {
      this._selectedEndDate = date;
    }
    this._updateTimeInputs();
  }

  /**
   *
   * Returns the user selected date based on _activeDateType.
   * Can be null if user has not selected any date.
   *
   * @return currentActive Date
   * @private
   */
  _getActiveDate () {
    return this._activeDateType === 'START'
      ? this._selectedStartDate
      : this._selectedEndDate;
  }

  _isYearMonth () {
    return this._granularTickUnitOrder === TickUnitOrder[TickUnit.MONTHS];
  }

  _isYearOnly () {
    return this._granularTickUnitOrder === TickUnitOrder[TickUnit.YEARS];
  }

  _getAdjustedExtremeDates (isStart: boolean) {
    const minDate = isStart ? null : this._selectedStartDate || this._minDate;
    const maxDate = isStart ? this._selectedEndDate || this._maxDate : null;
    const earlierDate = minDate ? new Date(minDate) : minDate;
    const latestDate = maxDate ? new Date(maxDate) : maxDate;
    if (this._isYearMonth()) {
      if (earlierDate) {
        earlierDate.setDate(1);
      }
      if (latestDate) {
        latestDate.setMonth(latestDate.getMonth() + 1, 0);
      }
    } else if (this._isYearOnly()) {
      if (earlierDate) {
        earlierDate.setMonth(0, 1);
      }
      if (latestDate) {
        latestDate.setMonth(11, 31);
      }
    }
    return { earlierDate, latestDate };
  }

  _hasTime () {
    return this._isTickUnitEnabled(TickUnit.HOURS);
  }

  _isPressed (datePicker: HTMLElement) {
    return datePicker.getAttribute('aria-pressed') === 'true';
  }

  _isTickUnitEnabled (tickUnit: string) {
    return this._granularTickUnitOrder <= TickUnitOrder[tickUnit];
  }

  _parseAndValidateDateString (dateString: string) {
    const validDateFormats = [ this._dateFormat ];
    if (this._isTickUnitEnabled(TickUnit.SECONDS)) {
      validDateFormats.push('dd.MM.yyyy HH:mm:ss');
    }
    if (this._isTickUnitEnabled(TickUnit.MINUTES)) {
      validDateFormats.push('dd.MM.yyyy HH:mm');
    }
    if (this._isTickUnitEnabled(TickUnit.DAYS)) {
      validDateFormats.push('dd.MM.yyyy');
    }

    for (const dateFormat of validDateFormats) {
      const date = parseAndValidateDateWithJavaFormat(dateString.trim(), dateFormat);
      if (date) {
        return date;
      }
    }
  }

  _onMediaSmUpChanged (event: MediaQueryListEvent | MediaQueryList) {
    if (this._labelBetweenStartAndEnd) { // _labelBetweenStartAndEnd may not exist in non-popupMode
      this._labelBetweenStartAndEnd.textContent = event.matches ? this._longLabelText : this._shortLabelText;
    }
  }

  /**
   * Updates the text between the start date picker and the end date picker.
   *
   * @param longLabelText - The text to display on large screens.
   * @param shortLabelText - The text for small screens.
   */
  updateLabelBetweenStartAndEnd (longLabelText: string, shortLabelText?: string) {
    shortLabelText = shortLabelText || longLabelText; // If short label is not set, use long label.
    this._longLabelText = longLabelText;
    this._shortLabelText = shortLabelText;
    this._onMediaSmUpChanged(mediaSmUp);
  }

  /**
   * Returns the user selected dates or if not selected, the initial dates.
   *
   * @return Start and end date.
   */
  get values () {
    return {
      start: this._selectedStartDate || this._initialStartDate,
      end: this._selectedEndDate || this._initialEndDate
    };
  }

}

customElements.define(COMPONENT_NAME, DateRangePicker);
